import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const SchoolList = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/schools');
      setSchools(res.data);
    } catch (err) {
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this school?')) return;
    try {
      await api.delete(`/schools/${id}`);
      toast.success('School deleted successfully');
      fetchSchools();
    } catch (err) {
      toast.error('Failed to delete school');
    }
  };

  const filteredSchools = schools.filter(s => {
    const term = search.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(term) ||
      (s.udise || '').toLowerCase().includes(term) ||
      (s.board_name || '').toLowerCase().includes(term) ||
      (s.city || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredSchools.length / itemsPerPage);
  const paginatedSchools = filteredSchools.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <AppLayout title="Schools">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ position: 'relative', width: 250 }}>
          <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search schools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
          />
        </div>
        <button
          onClick={() => navigate('/school/add')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
        >
          <Plus size={18} /> Add School
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>ID</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>UDISE</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Board</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>City/State</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Contact</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ padding: 40, textAlign: 'center', color: C.g400 }}>Loading...</td></tr>
              ) : paginatedSchools.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
                    No schools found.
                  </td>
                </tr>
              ) : (
                paginatedSchools.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                    <td style={{ padding: '16px 20px', fontSize: 14, color: C.g500 }}>#{s.id}</td>
                    <td style={{ padding: '16px 20px', fontSize: 14, fontWeight: 500, color: C.g900 }}>{s.name}</td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>{s.udise || '-'}</td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>{s.board_name || '-'}</td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      {s.city || '-'}, {s.state || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      {s.contact1 || s.email || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <button onClick={() => setSelectedSchool(s)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.g500, marginRight: 8 }} title="View School Details">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => navigate(`/school/edit/${s.id}`)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.g400, marginRight: 8 }} title="Edit School">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#EF4444' }} title="Delete School">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', borderTop: `1px solid ${C.g100}`, gap: 8 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: 14, color: C.g600 }}>Page {currentPage} of {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedSchool && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 16, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.g900 }}>School Details</h2>
              <button onClick={() => setSelectedSchool(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, color: C.g500, lineHeight: 1 }}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: 12, fontSize: 14, color: C.g700 }}>
              <div><strong style={{color: C.g900}}>ID:</strong> {selectedSchool.id}</div>
              <div><strong style={{color: C.g900}}>Name:</strong> {selectedSchool.name}</div>
              <div><strong style={{color: C.g900}}>UDISE:</strong> {selectedSchool.udise || '-'}</div>
              <div><strong style={{color: C.g900}}>Board:</strong> {selectedSchool.board_name || '-'}</div>
              <div><strong style={{color: C.g900}}>Type:</strong> {selectedSchool.school_type || '-'}</div>
              <div><strong style={{color: C.g900}}>City:</strong> {selectedSchool.city || '-'}</div>
              <div><strong style={{color: C.g900}}>State:</strong> {selectedSchool.state || '-'}</div>
              <div><strong style={{color: C.g900}}>Contact 1:</strong> {selectedSchool.contact1 || '-'}</div>
              <div><strong style={{color: C.g900}}>Contact 2:</strong> {selectedSchool.contact2 || '-'}</div>
              <div><strong style={{color: C.g900}}>Email:</strong> {selectedSchool.email || '-'}</div>
              <div><strong style={{color: C.g900}}>Address:</strong> {selectedSchool.street1 || '-'} {selectedSchool.street2 || ''}</div>
              <div><strong style={{color: C.g900}}>Landmark:</strong> {selectedSchool.landmark || '-'}</div>
              <div><strong style={{color: C.g900}}>Status:</strong> {selectedSchool.status || 'Active'}</div>
            </div>
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <button onClick={() => setSelectedSchool(null)} style={{ padding: '8px 16px', background: C.g100, color: C.g800, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SchoolList;
